/**
 * "Always fullscreen" on phones.
 *
 * iPhone Safari has no Fullscreen API and in landscape its toolbars eat the screen. The only
 * way to collapse them is a user scroll gesture, so while they're showing we:
 *   - show a small "Swipe up to go full screen" bar,
 *   - make the document briefly scrollable (tall spacer + a transparent pan-y swipe layer above
 *     the game, since every game layer uses touch-action:none),
 * and once the visual viewport fills the screen we hide the prompt but KEEP the page scrolled
 * (Safari re-shows its bars if you scroll back to the top), blocking manual scrolling instead.
 * It re-arms whenever the toolbars come back (tap on the top/bottom edge) or the orientation
 * changes. Portrait keeps the rotate prompt. Add ?fsdebug=1 to see live viewport numbers.
 *
 * Android Chrome / iPad (with the Fullscreen API): request fullscreen + landscape lock on tap.
 */
import { el } from './dom';

type FsDoc = Document & { webkitFullscreenElement?: Element; webkitFullscreenEnabled?: boolean };
type FsEl = HTMLElement & { webkitRequestFullscreen?: () => void };

export function fullscreenApiAvailable(): boolean {
  const d = document as FsDoc;
  return !!(d.fullscreenEnabled || d.webkitFullscreenEnabled);
}

export function isFullscreen(): boolean {
  const d = document as FsDoc;
  return !!(document.fullscreenElement || d.webkitFullscreenElement);
}

export function isIOS(): boolean {
  const ua = navigator.userAgent;
  const iPhone = /iPhone|iPod/.test(ua);
  const iPad = /iPad/.test(ua) || (navigator.platform === 'MacIntel' && navigator.maxTouchPoints > 1);
  return iPhone || iPad;
}

export function isStandalone(): boolean {
  return (navigator as Navigator & { standalone?: boolean }).standalone === true || matchMedia('(display-mode: standalone), (display-mode: fullscreen)').matches;
}

export async function requestFullscreen(): Promise<void> {
  if (isFullscreen() || !fullscreenApiAvailable()) return;
  const r = document.documentElement as FsEl;
  try {
    if (r.requestFullscreen) await r.requestFullscreen({ navigationUI: 'hide' });
    else r.webkitRequestFullscreen?.();
    await (screen.orientation as ScreenOrientation & { lock?: (o: string) => Promise<void> })?.lock?.('landscape');
  } catch {
    /* denied / unsupported — fine */
  }
}

export class FullscreenHelper {
  /** iOS "swipe up" bar + swipe layer. */
  readonly root = el('div', 'ios-swipe', `<div class="ios-swipe-bar"><span class="arr"><i></i><i></i></span><span>Swipe up to go full screen</span><button class="ios-swipe-x" aria-label="Not now"><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round"><path d="M6 6l12 12M18 6L6 18"/></svg></button></div>`);
  /** Dismissed with ✕ — stays hidden until the next orientation change. */
  private dismissed = false;
  private wasLandscape?: boolean;
  private spacer = el('div', 'ios-swipe-spacer');
  /** Swipe prompt currently shown. */
  private active = false;
  /** iOS landscape: document kept scrollable (spacer present) so Safari can keep its bars collapsed. */
  private armed = false;
  private touching = false;
  private readonly ios: boolean;
  private checkTimer = 0;
  private armedAndroid = true;
  private readonly debug = new URLSearchParams(location.search).get('fsdebug') === '1';
  /** Fired when the prompt appears/disappears (UI pauses gameplay while it's up). */
  onChange?: (showing: boolean) => void;

  constructor(private readonly isTouch: boolean) {
    // iPads with a Fullscreen API use it instead of the swipe trick.
    this.ios = isIOS() && !fullscreenApiAvailable() && !isStandalone();
    if (this.ios) {
      // IMPORTANT (why the prompt used to keep popping back): Safari only keeps its toolbars
      // collapsed while the document is scrolled. Scrolling back to the top — or removing the
      // spacer so the page can't scroll — brings the bars straight back. So once collapsed we
      // leave the scroll position alone, keep the spacer, and block manual scrolling instead.
      const check = () => this.scheduleCheck();
      window.addEventListener('resize', check);
      window.addEventListener('orientationchange', () => {
        check();
        setTimeout(check, 400);
        setTimeout(check, 1000);
      });
      window.visualViewport?.addEventListener('resize', check);
      window.addEventListener('scroll', check, { passive: true });
      document.addEventListener('touchstart', () => (this.touching = true), { passive: true, capture: true });
      const end = () => {
        this.touching = false;
        check();
      };
      document.addEventListener('touchend', end, { passive: true, capture: true });
      document.addEventListener('touchcancel', end, { passive: true, capture: true });
      // With the page armed (scrollable), stop gameplay touches from scrolling it — except inside
      // genuinely scrollable UI panels (settings, codex lists).
      document.addEventListener(
        'touchmove',
        (e) => {
          if (!this.armed || this.active) return;
          if (!scrollableAncestor(e.target as Element | null)) e.preventDefault();
        },
        { passive: false },
      );
      this.root.querySelector('.ios-swipe-x')!.addEventListener('click', (e) => {
        e.stopPropagation();
        this.dismissed = true;
        this.setPrompt(false);
      });
      this.check();
      if (this.debug) setInterval(() => this.renderDebug(), 250);
    }
    // Android Chrome & friends: first tap (and the first tap after leaving fullscreen) goes fullscreen.
    if (!this.ios && this.isTouch && fullscreenApiAvailable()) {
      window.addEventListener(
        'pointerdown',
        () => {
          if (this.armedAndroid && !isFullscreen()) {
            this.armedAndroid = false;
            void requestFullscreen();
          }
        },
        true,
      );
      document.addEventListener('fullscreenchange', () => {
        if (!isFullscreen()) this.armedAndroid = true;
      });
    }
  }

  get showing(): boolean {
    return this.active;
  }

  /** Debounced: only decide once the viewport has settled (Safari animates its bars). */
  private scheduleCheck(): void {
    if (!this.ios) return;
    clearTimeout(this.checkTimer);
    this.checkTimer = window.setTimeout(() => this.check(), 300);
  }

  /** True when Safari's toolbars are eating part of the landscape screen. */
  toolbarsShowing(): boolean {
    if (!isLandscape()) return false;
    // iOS reports screen.width/height in portrait terms; the landscape height is the short side.
    const full = Math.min(screen.width, screen.height);
    const vh = Math.max(window.innerHeight, window.visualViewport?.height ?? 0);
    const tolerance = Math.max(16, full * 0.06);
    return vh < full - tolerance;
  }

  private setArmed(on: boolean): void {
    if (on === this.armed) return;
    this.armed = on;
    document.documentElement.classList.toggle('ios-scroll', on);
    if (on) document.body.append(this.spacer);
    else {
      this.spacer.remove();
      window.scrollTo(0, 0);
    }
  }

  private setPrompt(on: boolean): void {
    if (on === this.active) return;
    this.active = on;
    document.documentElement.classList.toggle('ios-swipe-on', on);
    this.root.classList.toggle('show', on);
    // The bars are already visible here, so resetting the scroll is harmless and guarantees
    // there is room below for the next swipe. NEVER scroll when hiding the prompt.
    if (on && window.scrollY > window.innerHeight * 0.5) window.scrollTo(0, 0);
    this.onChange?.(on);
  }

  check(): void {
    if (!this.ios) return;
    const landscape = isLandscape();
    // ✕ dismissal lasts until the device actually rotates (not when the bars come back).
    if (landscape !== this.wasLandscape) {
      this.wasLandscape = landscape;
      this.dismissed = false;
    }
    this.setArmed(landscape);
    if (!landscape) {
      this.setPrompt(false);
      return;
    }
    if (this.touching) return; // decide after the finger lifts
    const bars = this.toolbarsShowing();
    // Dismissed with ✕ (or bars gone) → hide; the prompt stays away until the next orientation change.
    this.setPrompt(bars && !this.dismissed);
  }

  private dbg?: HTMLElement;
  private renderDebug(): void {
    if (!this.dbg) {
      this.dbg = el('div', '');
      this.dbg.style.cssText = 'position:fixed;left:4px;bottom:4px;z-index:9999;font:11px monospace;color:#9f9;background:rgba(0,0,0,.7);padding:4px;pointer-events:none';
      document.body.append(this.dbg);
    }
    const vv = window.visualViewport;
    this.dbg.textContent = `inner ${innerWidth}x${innerHeight} vv ${vv ? `${Math.round(vv.width)}x${Math.round(vv.height)}` : '-'} screen ${screen.width}x${screen.height} scrollY ${Math.round(scrollY)} armed ${this.armed} prompt ${this.active} bars ${this.toolbarsShowing()}`;
  }
}

function isLandscape(): boolean {
  return window.innerWidth > window.innerHeight;
}

function scrollableAncestor(t: Element | null): boolean {
  for (let n = t; n && n !== document.body && n !== document.documentElement; n = n.parentElement) {
    const oy = getComputedStyle(n).overflowY;
    if ((oy === 'auto' || oy === 'scroll') && n.scrollHeight > n.clientHeight + 1) return true;
  }
  return false;
}
